import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { FieldArrayType, FieldArrayTypeConfig } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import { ApplicationState } from "@vp/data-access/application";
import { OrganizationFeatures } from "@vp/models";
import { FeatureService } from "@vp/shared/features";
import { filterNullMap } from "@vp/shared/operators";
import { getUtcNow } from "@vp/shared/utilities";
import { BehaviorSubject } from "rxjs";
import { map, take, tap, withLatestFrom } from "rxjs/operators";
import { AddOrEditNavigatorContactComponent } from "../add-or-edit-navigator-contact/add-or-edit-navigator-contact.component";

export interface NavigatorContact {
  active: boolean;
  contactType: string;
  result: string;
  notes: string;
  createdDateTime: string;
  createdBy: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
}

@Component({
  selector: "vp-web-app-navigator-contacts",
  standalone: false,
  templateUrl: "./navigator-contacts.component.html",
  styleUrls: ["./navigator-contacts.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigatorContactsComponent
  extends FieldArrayType<FieldArrayTypeConfig>
  implements OnInit
{
  displayedColumns = ["createdDateTime", "contactType", "result", "notes", "actions"];

  private _navigatorContacts$ = new BehaviorSubject<NavigatorContact[]>([]);
  constructor(
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly featureService: FeatureService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  navigatorContacts$ = this._navigatorContacts$.pipe(
    map(contacts => contacts.filter(c => c.active))
  );

  canDeleteContact$ = this.featureService
    .configurationListValue$(OrganizationFeatures.navigatorPortal, "canDeleteContactRoles")
    .pipe(
      map(roles => {
        const loggedInUserRole = this.store.selectSnapshot(ApplicationState.selectedRole);
        return roles.some(role => role === loggedInUserRole?.friendlyId);
      })
    );

  ngOnInit(): void {
    this._navigatorContacts$.next((this.formControl?.value ?? []) as NavigatorContact[]);
  }

  addOrEditContact(action: "Add" | "Edit", contact?: NavigatorContact) {
    this.dialog
      .open(AddOrEditNavigatorContactComponent, {
        width: "500px",
        data: { action: action, contact: contact },
        panelClass: "mat-mdc-dialog-basic"
      })
      .afterClosed()
      .pipe(
        take(1),
        filterNullMap(),
        withLatestFrom(this._navigatorContacts$),
        tap(([contact, contacts]: [NavigatorContact, NavigatorContact[]]) => {
          const indexToUpdate = contacts.indexOf(contact);
          if (action === "Add") {
            if (this.formControl) {
              this.add();
            }
          }
          const result =
            action === "Add"
              ? [...contacts, contact]
              : contacts.map((item, index) => (index === indexToUpdate ? contact : item));
          this.updateForm(result);
        })
      )
      .subscribe();
  }

  removeContact(contactToRemove: NavigatorContact) {
    const loggedInUser = this.store.selectSnapshot(ApplicationState.loggedInUser);
    if (loggedInUser) {
      this._navigatorContacts$
        .pipe(
          take(1),
          tap(contacts => {
            const indexToRemove = contacts.indexOf(contactToRemove);
            if (indexToRemove !== -1) {
              const result = contacts.map((item, index) =>
                index === indexToRemove
                  ? {
                      ...item,
                      active: false,
                      lastUpdatedBy: loggedInUser.email,
                      lastUpdatedDateTime: getUtcNow()
                    }
                  : item
              );
              this.updateForm(result);
            }
          })
        )
        .subscribe();
    }
  }

  private updateForm(contacts: NavigatorContact[]) {
    this.formControl?.setValue(contacts);
    this._navigatorContacts$.next(contacts);
    this.cdr.markForCheck();
  }
}
